import { Controller } from "@hotwired/stimulus"

// Show/hide for the always-present side panel.
// Listens for side-panel:open / side-panel:close events dispatched from
// elsewhere in the page (e.g. intern-task-row's openSidePanel).
export default class extends Controller {
  static targets = ["panel", "saveStatus"]
  static values = { taskId: String }

  connect() {
    this.boundOpen = () => this.show()
    this.boundClose = () => this.close()
    this.boundSaving = () => this.showSaveStatus("Saving…", "#8C8C8C")
    this.boundSaved = () => this.showSaveStatus("Saved ✓", "#9CAF88", 2000)
    this.boundSaveError = () => this.showSaveStatus("Save failed", "#E57373", 3000)
    this.element.addEventListener("side-panel:open", this.boundOpen)
    this.element.addEventListener("side-panel:close", this.boundClose)
    document.addEventListener("panel:saving", this.boundSaving)
    document.addEventListener("panel:saved", this.boundSaved)
    document.addEventListener("panel:save-error", this.boundSaveError)
  }

  disconnect() {
    this.element.removeEventListener("side-panel:open", this.boundOpen)
    this.element.removeEventListener("side-panel:close", this.boundClose)
    document.removeEventListener("panel:saving", this.boundSaving)
    document.removeEventListener("panel:saved", this.boundSaved)
    document.removeEventListener("panel:save-error", this.boundSaveError)
  }

  showSaveStatus(text, color, autoHideMs = null) {
    if (!this.hasSaveStatusTarget) return
    clearTimeout(this.saveStatusTimer)
    this.saveStatusTarget.textContent = text
    this.saveStatusTarget.style.color = color
    this.saveStatusTarget.style.opacity = "1"
    if (autoHideMs) {
      this.saveStatusTimer = setTimeout(() => {
        this.saveStatusTarget.style.opacity = "0"
      }, autoHideMs)
    }
  }

  show() {
    // Cancel any pending close-hide from a recent close() — without this
    // a rapid close→open cycle re-hides the panel mid-animation.
    clearTimeout(this.closeTimer)
    this.element.hidden = false
    requestAnimationFrame(() => {
      this.element.style.transform = "translateX(0)"
    })
  }

  close() {
    this.element.style.transform = "translateX(100%)"
    this.closeTimer = setTimeout(() => {
      this.element.hidden = true
    }, 220)
  }

  async deleteTask(event) {
    event.preventDefault()
    const button = event.currentTarget
    const taskId = button.dataset.sidePanelTaskIdValue
    if (!taskId) return

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      // Accept: application/json so the controller responds with head :ok
      // rather than 302 → /admin/intern_tasks (which fetch would follow
      // with the same DELETE method and 404 on the routing).
      const response = await fetch(`/admin/intern_tasks/${taskId}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrf, "Accept": "application/json" }
      })
      if (!response.ok) throw new Error(`Delete failed: ${response.status}`)
      window.location.reload()
    } catch (err) {
      console.error("deleteTask failed:", err)
      alert("Could not delete task.")
    }
  }

  async archiveTask(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.sidePanelArchiveUrlValue
    if (!url) return
    if (!window.confirm("Archive this task? It will move to the Archived view.")) return
    await this._patchAndReload(url, "Could not archive task.")
  }

  async unarchiveTask(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.sidePanelUnarchiveUrlValue
    if (!url) return
    await this._patchAndReload(url, "Could not unarchive task.")
  }

  async _patchAndReload(url, errMsg) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: { "X-CSRF-Token": csrf, "Accept": "application/json" }
      })
      if (!response.ok) throw new Error(`${response.status}`)
      window.location.reload()
    } catch (err) {
      console.error("archive/unarchive failed:", err)
      alert(errMsg)
    }
  }
}
